/**
 * Decode a `relates_to_urn` returned by the graph `search` query into a
 * Morpheus route. This is the client's *only* piece of routing knowledge — the
 * server decides which entity a query resolved to and hands back a URN; the
 * client just maps URN -> path.
 *
 * URN shape: `adc:<resource>:<id>` (e.g. `adc:property:889`).
 *
 * `fullRefresh` is derived from the destination's owning app in the MANIFEST
 * (via `requiresFullRefresh`): a route owned by another MFE needs a hard
 * navigation, one owned by this app can use an in-app history push. We no
 * longer hand-set it here.
 */

import Urn from '@adc/structures/urn'
import { MANIFEST, requiresFullRefresh } from '../../authorization/manifest'

export interface DecodedRoute {
  readonly path: string
  readonly fullRefresh: boolean
}

type RouteBuilder = (id: string) => DecodedRoute

// Which MANIFEST entry owns the destination path for each URN resource. The
// owning app on that entry drives fullRefresh.
const fr = (key: keyof typeof MANIFEST): boolean =>
  requiresFullRefresh(MANIFEST[key])

const ROUTE_BY_RESOURCE: Record<string, RouteBuilder> = {
  // All transactions for a property (the flagship "paste address -> Enter" flow).
  property: id => ({
    path: `/transactions/property/${id}`,
    fullRefresh: fr('transaction'),
  }),
  // A single transaction.
  transaction: id => ({
    path: `/transactions/${id}`,
    fullRefresh: fr('transaction'),
  }),
  // A listing resolves through its property redirect route.
  listing: id => ({
    path: `/transactions/property/listing/${id}`,
    fullRefresh: fr('transaction'),
  }),
  // Scheduling entities key on internal id (server already resolved code->id).
  event: id => ({
    path: `/schedule/events/${id}/edit`,
    fullRefresh: fr('event'),
  }),
  venue: id => ({
    path: `/schedule/venues/${id}/edit`,
    fullRefresh: fr('venue'),
  }),
  // Admin entities.
  user: id => ({
    path: `/user-management/${id}/edit`,
    fullRefresh: fr('user'),
  }),
  organization: id => ({
    path: `/organizations/${id}/edit`,
    fullRefresh: fr('organization'),
  }),
}

/**
 * @returns the decoded route, or null when the URN is malformed or its
 * resource type has no known Morpheus route.
 */
export function urnToRoute(
  urn: string | null | undefined
): DecodedRoute | null {
  if (!urn) return null
  const parsed = Urn.safelyParse(urn)
  if (!parsed) return null
  if (parsed.namespace !== 'adc' && parsed.namespace !== 'urn') return null
  const id = parsed.identifier
  if (!id) return null
  const builder = ROUTE_BY_RESOURCE[parsed.resourceType.toLowerCase()]
  return builder ? builder(id) : null
}
